import apiClient from './client'
import axios from 'axios'

export type TaskStatus = 'new' | 'in_progress' | 'completed'

export interface UserItem {
  id: number
  name: string
  email: string
}

export interface TaskChecklistItem {
  id: number
  task_id: number
  title: string
  is_completed: boolean
  position: number
}

export interface TaskAttachment {
  id: number
  task_id: number
  original_filename: string
  file_size: number
  mime_type?: string
  created_at?: string
}

export interface TaskRelation {
  id: number
  title: string
  status: TaskStatus
  deadline: string | null
}

export interface TaskCommentAttachment {
  id: number
  comment_id: number
  original_filename: string
  file_size: number
  mime_type?: string
  created_at?: string
}

export interface TaskCommentItem {
  id: number
  task_id: number
  user_id: number | null
  user_name: string | null
  user_email: string | null
  content: string
  created_at: string
  updated_at: string
  attachments: TaskCommentAttachment[]
}

export interface TaskItem {
  id: number
  project_id: number
  title: string
  description: string | null
  status: TaskStatus
  deadline: string | null
  created_by: number | null
  creator_name?: string | null
  status_changer_name?: string | null
  assignees?: UserItem[]
  assignee_names?: string | null
  assignee_ids?: string | null
  parent_id: number | null
  parent_title?: string | null
  created_at: string
  updated_at: string
  checklist_total?: number
  checklist_completed?: number
  checklist?: TaskChecklistItem[]
  attachments?: TaskAttachment[]
  relations?: TaskRelation[]
  subtasks?: TaskRelation[]
}

export const getTasks = async (projectId: number): Promise<TaskItem[]> => {
  const response = await apiClient.get('/api/tasks', {
    params: { project_id: projectId },
  })
  return Array.isArray(response.data) ? response.data : []
}

export const getTasksUsers = async (): Promise<UserItem[]> => {
  const response = await apiClient.get('/api/tasks/users')
  return Array.isArray(response.data) ? response.data : []
}

export const createTask = async (data: {
  project_id: number
  title: string
  description?: string
  status?: TaskStatus
  deadline?: string | null
  assignee_ids?: number[]
  parent_id?: number | null
  checklist?: string[]
}): Promise<{ id: number }> => {
  const response = await apiClient.post('/api/tasks', data)
  return response.data
}

export const getTask = async (taskId: number): Promise<TaskItem> => {
  const response = await apiClient.get(`/api/tasks/${taskId}`)
  return response.data
}

export const updateTask = async (taskId: number, data: Partial<Omit<TaskItem, 'assignee_ids'>> & { assignee_ids?: number[] }): Promise<void> => {
  await apiClient.put(`/api/tasks/${taskId}`, data)
}

export const deleteTask = async (taskId: number): Promise<void> => {
  await apiClient.delete(`/api/tasks/${taskId}`)
}

export const addChecklistItem = async (taskId: number, title: string): Promise<{ id: number }> => {
  const response = await apiClient.post(`/api/tasks/${taskId}/checklist`, { title })
  return response.data
}

export const updateChecklistItem = async (
  itemId: number,
  data: { title?: string; is_completed?: boolean | number; position?: number }
): Promise<void> => {
  const payload = { ...data }
  if (typeof payload.is_completed === 'boolean') {
    payload.is_completed = payload.is_completed ? 1 : 0
  }
  await apiClient.put(`/api/tasks/checklist/${itemId}`, payload)
}

export const deleteChecklistItem = async (itemId: number): Promise<void> => {
  await apiClient.delete(`/api/tasks/checklist/${itemId}`)
}

export const getPotentialRelations = async (taskId: number): Promise<TaskRelation[]> => {
  const response = await apiClient.get(`/api/tasks/${taskId}/relations`)
  return Array.isArray(response.data) ? response.data : []
}

export const addTaskRelation = async (taskId: number, relatedTaskId: number): Promise<void> => {
  await apiClient.post(`/api/tasks/${taskId}/relations`, { related_task_id: relatedTaskId })
}

export const deleteTaskRelation = async (taskId: number, relatedTaskId: number): Promise<void> => {
  await apiClient.delete(`/api/tasks/${taskId}/relations/${relatedTaskId}`)
}

export const uploadTaskAttachments = async (taskId: number, files: File[]): Promise<TaskAttachment[]> => {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })

  const runtimeBaseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const cleanAxios = axios.create({
    baseURL: runtimeBaseUrl,
    timeout: 10 * 60 * 1000,
  })
  
  const response = await cleanAxios.post(`/api/tasks/${taskId}/attachments`, formData)
  if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
    return Array.isArray(response.data.data) ? response.data.data : []
  }
  if (response.data && typeof response.data === 'object' && 'error' in response.data) {
    throw new Error(String(response.data.error))
  }
  return Array.isArray(response.data) ? response.data : []
}

export const deleteTaskAttachment = async (attachmentId: number): Promise<void> => {
  await apiClient.delete(`/api/tasks/attachments/${attachmentId}`)
}

export const getTaskAttachmentViewUrl = (attachmentId: number): string => 
  `/api/tasks/attachments/${attachmentId}?mode=view`

export const getTaskAttachmentDownloadUrl = (attachmentId: number): string => 
  `/api/tasks/attachments/${attachmentId}?mode=download`

// Comments API
export const getTaskComments = async (taskId: number): Promise<TaskCommentItem[]> => {
  const response = await apiClient.get(`/api/tasks/${taskId}/comments`)
  return Array.isArray(response.data) ? response.data : []
}

export const createTaskComment = async (taskId: number, content: string): Promise<{ id: number }> => {
  const response = await apiClient.post(`/api/tasks/${taskId}/comments`, { content })
  return response.data
}

export const deleteTaskComment = async (commentId: number): Promise<void> => {
  await apiClient.delete(`/api/tasks/comments/${commentId}`)
}

export const updateTaskComment = async (commentId: number, content: string): Promise<void> => {
  await apiClient.put(`/api/tasks/comments/${commentId}`, { content })
}

export const uploadTaskCommentAttachments = async (commentId: number, files: File[]): Promise<TaskCommentAttachment[]> => {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })

  const runtimeBaseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const cleanAxios = axios.create({
    baseURL: runtimeBaseUrl,
    timeout: 10 * 60 * 1000,
  })
  
  const response = await cleanAxios.post(`/api/tasks/comments/${commentId}/attachments`, formData)
  if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
    return Array.isArray(response.data.data) ? response.data.data : []
  }
  if (response.data && typeof response.data === 'object' && 'error' in response.data) {
    throw new Error(String(response.data.error))
  }
  return Array.isArray(response.data) ? response.data : []
}

export const deleteTaskCommentAttachment = async (attachmentId: number): Promise<void> => {
  await apiClient.delete(`/api/tasks/comments/attachments/${attachmentId}`)
}

export const getTaskCommentAttachmentViewUrl = (attachmentId: number): string => 
  `/api/tasks/comments/attachments/${attachmentId}?mode=view`

export const getTaskCommentAttachmentDownloadUrl = (attachmentId: number): string => 
  `/api/tasks/comments/attachments/${attachmentId}?mode=download`
